import {
  createContext,
  PropsWithChildren,
  ReactElement,
  useContext,
  useMemo
} from 'react'
import { checkDependencies } from './checkDependencies'
import { UrlParams } from 'utils/url/url.interface'

export type DependencyCheck<T> = (
  state: T,
  leafValue: any,
  currentPage: string,
  urlParams: UrlParams
) => boolean

export type DependencyCheckOrTree<T> =
  | DependencyCheck<T>
  | Record<string, DependencyCheck<T> | Record<string, DependencyCheck<T>>>

export type Dependencies<T> = Record<
  string,
  DependencyCheckOrTree<T> | DependencyCheckOrTree<T>[]
>

export type FlatDependencies<T> = Record<string, DependencyCheckOrTree<T>>

export interface Props<T> {
  state: T | any
  dependencies: Dependencies<T>
  currentPageInSession: string
  urlParams: UrlParams
}

export const DependencyContext = createContext<Props<any>>({
  state: {},
  currentPageInSession: '',
  dependencies: {},
  urlParams: {}
})

export const DependencyProvider = <T,>({
  children,
  ...value
}: PropsWithChildren<Props<T>>): ReactElement => (
  <DependencyContext.Provider value={value}>
    {children}
  </DependencyContext.Provider>
)

export const useConditionalRender = (path: string): boolean => {
  const { state, dependencies, currentPageInSession, urlParams } =
    useContext(DependencyContext)

  return useMemo(
    () =>
      checkDependencies(
        path,
        state,
        dependencies,
        currentPageInSession,
        urlParams
      ),
    [path, state, dependencies, currentPageInSession, urlParams]
  )
}
